const MONTHS = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

const WEEKDAYS = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

const TIME_RE = /(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?/i;

function cloneDate(d) {
  return new Date(d.getTime());
}

function atLocal(date, hours, minutes) {
  const next = cloneDate(date);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

function parseTimeToken(raw, fallbackHour = 9) {
  if (!raw) return { hours: fallbackHour, minutes: 0, hadTime: false };
  const m = String(raw).trim().match(TIME_RE);
  if (!m) return { hours: fallbackHour, minutes: 0, hadTime: false };
  let hours = Number(m[1]);
  const minutes = m[2] != null ? Number(m[2]) : 0;
  const ampm = (m[3] || '').toLowerCase();
  if (ampm === 'pm' && hours < 12) hours += 12;
  if (ampm === 'am' && hours === 12) hours = 0;
  if (!ampm && hours <= 7) hours += 12;
  if (hours > 23 || minutes > 59) return { hours: fallbackHour, minutes: 0, hadTime: false };
  return { hours, minutes, hadTime: true };
}

function nextWeekday(from, weekday, requireFuture = true) {
  const next = cloneDate(from);
  const delta = (weekday - from.getDay() + 7) % 7;
  next.setDate(from.getDate() + (delta === 0 && requireFuture ? 7 : delta));
  return next;
}

function dayThisOrNextMonth(from, day) {
  const next = cloneDate(from);
  next.setDate(day);
  if (next.getDate() !== day) return null;
  if (next.getTime() < from.getTime()) {
    next.setMonth(next.getMonth() + 1);
    next.setDate(day);
    if (next.getDate() !== day) return null;
  }
  return next;
}

/**
 * Parse a natural-language deadline from the end of a title.
 * "milk today 6.00 pm" → title "milk", due 18:00 today.
 */
export function parseDeadlineFromText(input, now = new Date()) {
  const original = String(input || '').trim();
  if (!original) return { title: '', dueAt: null, reminderAt: null, label: null };

  const patterns = [
    {
      re: /(?:^|\s+)\bin\s+(\d+)\s*(minutes?|mins?|hours?|hrs?)\s*$/i,
      apply: (m) => {
        const n = Number(m[1]);
        const unit = m[2].toLowerCase();
        const due = cloneDate(now);
        if (unit.startsWith('hour') || unit.startsWith('hr')) due.setHours(due.getHours() + n);
        else due.setMinutes(due.getMinutes() + n);
        return due;
      },
    },
    {
      re: /(?:^|\s+)\b(today|tonight|tomorrow|tmr)\b(?:\s+(?:at\s+)?(\d{1,2}(?:[.:]\d{2})?\s*(?:am|pm)?))?$/i,
      apply: (m) => {
        const word = m[1].toLowerCase();
        const due = cloneDate(now);
        if (word === 'tomorrow' || word === 'tmr') due.setDate(due.getDate() + 1);
        const fallback = word === 'tonight' ? 20 : 9;
        const t = parseTimeToken(m[2], fallback);
        return atLocal(due, t.hours, t.minutes);
      },
    },
    {
      re: /(?:^|\s+)\b(?:next\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tue|tues|wed|thu|thur|thurs|fri|sat)\b(?:\s+(?:at\s+)?(\d{1,2}(?:[.:]\d{2})?\s*(?:am|pm)?))?$/i,
      apply: (m) => {
        const weekday = WEEKDAYS[m[1].toLowerCase()];
        const due = nextWeekday(now, weekday, true);
        const t = parseTimeToken(m[2], 9);
        return atLocal(due, t.hours, t.minutes);
      },
    },
    {
      re: /(?:^|\s+)\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?(?:\s+(?:at\s+)?(\d{1,2}(?:[.:]\d{2})?\s*(?:am|pm)?))?$/i,
      apply: (m) => {
        const month = MONTHS[m[1].toLowerCase()];
        const day = Number(m[2]);
        const due = cloneDate(now);
        due.setMonth(month, day);
        if (due.getTime() < now.getTime()) due.setFullYear(due.getFullYear() + 1);
        const t = parseTimeToken(m[3], 9);
        return atLocal(due, t.hours, t.minutes);
      },
    },
    {
      re: /(?:^|\s+)\b(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)(?:\s+(?:at\s+)?(\d{1,2}(?:[.:]\d{2})?\s*(?:am|pm)?))?$/i,
      apply: (m) => {
        const day = Number(m[1]);
        const t = parseTimeToken(m[2], 9);
        const candidate = dayThisOrNextMonth(atLocal(now, t.hours, t.minutes), day);
        return candidate;
      },
    },
    {
      re: /(?:^|\s+)\b(?:at\s+)?(\d{1,2}(?:[.:]\d{2})?\s*(?:am|pm))\s*$/i,
      apply: (m) => {
        const t = parseTimeToken(m[1], 9);
        let due = atLocal(now, t.hours, t.minutes);
        if (due.getTime() <= now.getTime()) {
          due = cloneDate(due);
          due.setDate(due.getDate() + 1);
        }
        return due;
      },
    },
  ];

  for (const pattern of patterns) {
    const match = original.match(pattern.re);
    if (!match) continue;
    const due = pattern.apply(match);
    if (!due || Number.isNaN(due.getTime())) continue;
    const title = original.slice(0, match.index).trim() || original;
    const dueAt = Math.floor(due.getTime() / 1000);
    return {
      title,
      dueAt,
      reminderAt: dueAt,
      label: formatDueLabel(due, now),
    };
  }

  return { title: original, dueAt: null, reminderAt: null, label: null };
}

export function formatDueLabel(date, now = new Date()) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date * 1000);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const tomorrow = cloneDate(now);
  tomorrow.setDate(now.getDate() + 1);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay(d, now)) return `Today ${time}`;
  if (sameDay(d, tomorrow)) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`;
}

export function secondsFromDateInput(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / 1000);
}

export function datetimeLocalValue(seconds) {
  if (!seconds) return '';
  const d = new Date(seconds * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function isDueSoon(item, now = new Date(), horizonDays = 2) {
  if (!item || item.completed || item.kind === 'folder') return false;
  if (item.urgent || item.pinned) return true;
  if (!item.due_at) return false;
  const horizon = cloneDate(now);
  horizon.setDate(horizon.getDate() + horizonDays);
  horizon.setHours(23, 59, 59, 999);
  return item.due_at * 1000 <= horizon.getTime();
}

export function isOverdue(item, now = new Date()) {
  if (!item || item.completed || !item.due_at) return false;
  return item.due_at * 1000 < now.getTime();
}
